import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { alpha } from "@mui/material/styles";
import PropTypes from "prop-types";

// @mui material components
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDAlert from "components/MDAlert";

// Layout
import PageLayout from "examples/LayoutContainers/PageLayout";

// Firebase
import { confirmPasswordReset, verifyPasswordResetCode } from "firebase/auth";
import { auth } from "../../../firebase";
import { nguoiDungApi } from "api/nguoiDung";
import { useTourContext } from "context/TourContext";
import useTour from "hooks/useTour";
import buocDatLaiMatKhau from "tours/datLaiMatKhau";

// Images
import logo from "assets/images/logo-thu-mua-bo.png";

// Cùng bảng màu/phông chữ thương hiệu với trang Đăng nhập.
import { MAU, FONT_HIEU, FONT_CHU } from "assets/theme/avocadoBrand";
import { oInputAuth, sxNutAuth } from "layouts/authentication/kieuTrangAuth";

// Thông báo lỗi Firebase Auth trả về cho oobCode — dịch sang tiếng Việt, map
// theo mã lỗi thay vì hiện thẳng message tiếng Anh của SDK.
function dichLoiFirebase(err) {
  const ma = err?.code ?? "";
  if (ma === "auth/expired-action-code") {
    return "Đường dẫn đặt lại mật khẩu này đã hết hạn. Hãy yêu cầu gửi lại email mới.";
  }
  if (ma === "auth/invalid-action-code") {
    return "Đường dẫn đặt lại mật khẩu không hợp lệ hoặc đã được dùng rồi.";
  }
  if (ma === "auth/user-disabled") {
    return "Tài khoản này đã bị vô hiệu hoá. Liên hệ quản trị viên để được hỗ trợ.";
  }
  if (ma === "auth/user-not-found") {
    return "Không tìm thấy tài khoản tương ứng với đường dẫn này.";
  }
  if (ma === "auth/weak-password") {
    return "Mật khẩu mới quá yếu. Hãy chọn mật khẩu khác dài hơn.";
  }
  return "Có lỗi xảy ra, không thể đặt lại mật khẩu. Hãy thử yêu cầu gửi lại email mới.";
}

// Lỗi khi tự yêu cầu gửi email đặt lại — cố tình KHÔNG dịch riêng
// "auth/user-not-found" thành thông báo khác nhóm còn lại, tránh lộ email nào
// đã có tài khoản trong hệ thống (user enumeration) trên trang công khai này.
function dichLoiGuiEmail(err) {
  const ma = err?.code ?? "";
  if (ma === "auth/invalid-email") {
    return "Địa chỉ email không hợp lệ.";
  }
  if (ma === "auth/too-many-requests") {
    return "Bạn vừa yêu cầu quá nhiều lần. Hãy thử lại sau ít phút.";
  }
  return "";
}

function NhanTruong({ children }) {
  return (
    <MDTypography
      variant="caption"
      sx={{
        display: "block",
        mb: 0.75,
        fontFamily: FONT_CHU,
        fontWeight: 600,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        color: alpha(MAU.ink, 0.65),
      }}
    >
      {children}
    </MDTypography>
  );
}

NhanTruong.propTypes = {
  children: PropTypes.node.isRequired,
};

function KhungTrang({ children }) {
  const { active: coTour, startActiveTour } = useTourContext();

  return (
    <PageLayout>
      <MDBox
        width="100%"
        minHeight="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
        sx={{ backgroundColor: MAU.paper, overflowX: "hidden" }}
      >
        <Grid container justifyContent="center" sx={{ px: { xs: 3, sm: 0 } }}>
          <Grid item xs={12} sm={9} md={6} lg={4}>
            <MDBox
              sx={{ width: "100%", maxWidth: "360px", mx: "auto", py: 6, position: "relative" }}
            >
              {coTour && (
                <IconButton
                  size="small"
                  aria-label="Xem lại hướng dẫn trang này"
                  onClick={startActiveTour}
                  sx={{
                    position: "absolute",
                    top: 40,
                    right: -8,
                    color: alpha(MAU.ink, 0.55),
                  }}
                >
                  <Icon>help_outline</Icon>
                </IconButton>
              )}
              <MDBox display="flex" alignItems="center" justifyContent="center" gap={1.5} mb={5}>
                <MDBox
                  component="img"
                  src={logo}
                  alt="Thu mua bơ"
                  sx={{ width: "64px", height: "64px", borderRadius: "50%" }}
                />
                <MDTypography
                  sx={{
                    fontFamily: FONT_HIEU,
                    fontWeight: 600,
                    fontSize: "1.3rem",
                    color: MAU.ink,
                  }}
                >
                  Thu mua bơ
                </MDTypography>
              </MDBox>
              {children}
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>
    </PageLayout>
  );
}

KhungTrang.propTypes = {
  children: PropTypes.node.isRequired,
};

function DatLaiMatKhau() {
  const [searchParams] = useSearchParams();
  const oobCode = searchParams.get("oobCode") ?? "";
  const coOobCode = oobCode !== "";

  // dangKiemTra: đang gọi verifyPasswordResetCode; hopLe: null = chưa biết,
  // true/false = kết quả kiểm tra oobCode. Chỉ áp dụng khi có oobCode (mở từ
  // link email) — không có oobCode nghĩa là user tự vào trang này để YÊU CẦU
  // gửi email, không phải lỗi (USC-01 nhánh 1B).
  const [dangKiemTra, setDangKiemTra] = useState(coOobCode);
  const [hopLe, setHopLe] = useState(null);
  const [email, setEmail] = useState("");
  const [loiKiemTra, setLoiKiemTra] = useState("");

  const [matKhauMoi, setMatKhauMoi] = useState("");
  const [xacNhanMatKhau, setXacNhanMatKhau] = useState("");
  const [hienMatKhau, setHienMatKhau] = useState(false);
  const [loiForm, setLoiForm] = useState("");
  const [dangGui, setDangGui] = useState(false);
  const [thanhCong, setThanhCong] = useState(false);

  // Form "yêu cầu gửi email" (không có oobCode).
  const [emailYeuCau, setEmailYeuCau] = useState("");
  const [loiYeuCau, setLoiYeuCau] = useState("");
  const [dangGuiYeuCau, setDangGuiYeuCau] = useState(false);
  const [daGuiYeuCau, setDaGuiYeuCau] = useState(false);

  useEffect(() => {
    if (!coOobCode) return;
    verifyPasswordResetCode(auth, oobCode)
      .then((emailTaiKhoan) => {
        setEmail(emailTaiKhoan);
        setHopLe(true);
      })
      .catch((err) => {
        setHopLe(false);
        setLoiKiemTra(dichLoiFirebase(err));
      })
      .finally(() => setDangKiemTra(false));
  }, [coOobCode, oobCode]);

  // Tour chỉ áp dụng cho màn hình ĐẦU TIÊN (form yêu cầu gửi email, khi mở
  // trang trực tiếp không kèm oobCode và chưa bấm gửi) — không cố tour cho
  // các trạng thái khác (đang kiểm tra oobCode, lỗi, đổi mật khẩu mới...).
  useTour("dat-lai-mat-khau", !coOobCode && !daGuiYeuCau ? buocDatLaiMatKhau() : []);

  async function handleSubmitYeuCau(e) {
    e.preventDefault();
    setLoiYeuCau("");
    setDangGuiYeuCau(true);
    try {
      await nguoiDungApi.guiEmailDatLaiMatKhau(emailYeuCau.trim());
    } catch (err) {
      const thongBao = dichLoiGuiEmail(err);
      // Lỗi "không tìm thấy tài khoản" cố tình KHÔNG hiện — coi như đã gửi để
      // tránh lộ email nào tồn tại trong hệ thống.
      if (thongBao) {
        setLoiYeuCau(thongBao);
        setDangGuiYeuCau(false);
        return;
      }
    }
    setDaGuiYeuCau(true);
    setDangGuiYeuCau(false);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setLoiForm("");

    if (matKhauMoi.length < 6) {
      setLoiForm("Mật khẩu mới phải có ít nhất 6 ký tự.");
      return;
    }
    if (matKhauMoi !== xacNhanMatKhau) {
      setLoiForm("Hai mật khẩu nhập vào không khớp nhau.");
      return;
    }

    setDangGui(true);
    try {
      await confirmPasswordReset(auth, oobCode, matKhauMoi);
      setThanhCong(true);
    } catch (err) {
      setLoiForm(dichLoiFirebase(err));
    } finally {
      setDangGui(false);
    }
  }

  const tieuDe = (text) => (
    <MDTypography
      sx={{ fontFamily: FONT_HIEU, fontWeight: 600, fontSize: "1.9rem", color: MAU.ink, mb: 0.5 }}
    >
      {text}
    </MDTypography>
  );

  if (!coOobCode) {
    if (daGuiYeuCau) {
      return (
        <KhungTrang>
          {tieuDe("Kiểm tra hộp thư của bạn")}
          <MDTypography
            sx={{ fontFamily: FONT_CHU, fontSize: "0.9rem", color: alpha(MAU.ink, 0.6) }}
          >
            Nếu <strong>{emailYeuCau.trim()}</strong> có tài khoản trong hệ thống, chúng tôi đã gửi
            một email chứa liên kết đặt lại mật khẩu. Mở email đó để tiếp tục.
          </MDTypography>
          <MDButton
            component={Link}
            to="/dang-nhap"
            variant="gradient"
            color="info"
            fullWidth
            sx={{ mt: 3, ...sxNutAuth }}
          >
            Quay lại đăng nhập
          </MDButton>
        </KhungTrang>
      );
    }

    return (
      <KhungTrang>
        {tieuDe("Quên mật khẩu")}
        <MDTypography
          sx={{ fontFamily: FONT_CHU, fontSize: "0.9rem", color: alpha(MAU.ink, 0.6), mb: 4 }}
        >
          Nhập email đăng nhập, chúng tôi sẽ gửi liên kết đặt lại mật khẩu.
        </MDTypography>

        <MDBox component="form" role="form" onSubmit={handleSubmitYeuCau}>
          <MDBox id="tour-dat-lai-mat-khau-email" mb={2.5}>
            <NhanTruong>Email</NhanTruong>
            <MDInput
              type="email"
              placeholder="ten@congty.vn"
              value={emailYeuCau}
              onChange={(e) => setEmailYeuCau(e.target.value)}
              fullWidth
              required
              sx={oInputAuth}
            />
          </MDBox>

          {loiYeuCau && (
            <MDBox mt={2}>
              <MDAlert color="error">{loiYeuCau}</MDAlert>
            </MDBox>
          )}

          <MDButton
            type="submit"
            variant="gradient"
            color="info"
            fullWidth
            disabled={dangGuiYeuCau}
            sx={{ mt: 4, ...sxNutAuth }}
          >
            {dangGuiYeuCau ? "Đang gửi…" : "Gửi liên kết đặt lại mật khẩu"}
          </MDButton>

          <MDTypography
            component={Link}
            to="/dang-nhap"
            sx={{
              display: "block",
              mt: 2.5,
              textAlign: "center",
              fontFamily: FONT_CHU,
              fontSize: "0.85rem",
              fontWeight: 600,
              color: MAU.skinDeep,
              textDecoration: "none",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            Quay lại đăng nhập
          </MDTypography>
        </MDBox>
      </KhungTrang>
    );
  }

  if (dangKiemTra) {
    return (
      <KhungTrang>
        <MDBox display="flex" flexDirection="column" alignItems="center" gap={2} py={4}>
          <CircularProgress sx={{ color: MAU.skin }} />
          <MDTypography
            sx={{ fontFamily: FONT_CHU, fontSize: "0.9rem", color: alpha(MAU.ink, 0.6) }}
          >
            Đang kiểm tra đường dẫn…
          </MDTypography>
        </MDBox>
      </KhungTrang>
    );
  }

  if (!hopLe) {
    return (
      <KhungTrang>
        {tieuDe("Không thể đặt lại mật khẩu")}
        <MDBox mt={2}>
          <MDAlert color="error">{loiKiemTra}</MDAlert>
        </MDBox>
        <MDButton
          component={Link}
          to="/dang-nhap"
          variant="gradient"
          color="info"
          fullWidth
          sx={{ mt: 3, ...sxNutAuth }}
        >
          Quay lại đăng nhập
        </MDButton>
      </KhungTrang>
    );
  }

  if (thanhCong) {
    return (
      <KhungTrang>
        {tieuDe("Đổi mật khẩu thành công")}
        <MDTypography sx={{ fontFamily: FONT_CHU, fontSize: "0.9rem", color: alpha(MAU.ink, 0.6) }}>
          Mật khẩu của {email} đã được cập nhật. Hãy đăng nhập lại bằng mật khẩu mới.
        </MDTypography>
        <MDButton
          component={Link}
          to="/dang-nhap"
          variant="gradient"
          color="info"
          fullWidth
          sx={{ mt: 3, ...sxNutAuth }}
        >
          Đăng nhập ngay
        </MDButton>
      </KhungTrang>
    );
  }

  return (
    <KhungTrang>
      {tieuDe("Đặt lại mật khẩu")}
      <MDTypography
        sx={{ fontFamily: FONT_CHU, fontSize: "0.9rem", color: alpha(MAU.ink, 0.6), mb: 4 }}
      >
        Đặt mật khẩu mới cho tài khoản <strong>{email}</strong>.
      </MDTypography>

      <MDBox component="form" role="form" onSubmit={handleSubmit}>
        <MDBox mb={2.5}>
          <NhanTruong>Mật khẩu mới</NhanTruong>
          <MDInput
            type={hienMatKhau ? "text" : "password"}
            placeholder="••••••••"
            value={matKhauMoi}
            onChange={(e) => setMatKhauMoi(e.target.value)}
            fullWidth
            required
            sx={oInputAuth}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setHienMatKhau((v) => !v)}
                    edge="end"
                    size="small"
                    tabIndex={-1}
                    aria-label={hienMatKhau ? "Ẩn mật khẩu" : "Hiện mật khẩu"}
                    sx={{ color: alpha(MAU.ink, 0.5) }}
                  >
                    <Icon fontSize="small">{hienMatKhau ? "visibility_off" : "visibility"}</Icon>
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </MDBox>

        <MDBox mb={1}>
          <NhanTruong>Xác nhận mật khẩu mới</NhanTruong>
          <MDInput
            type={hienMatKhau ? "text" : "password"}
            placeholder="••••••••"
            value={xacNhanMatKhau}
            onChange={(e) => setXacNhanMatKhau(e.target.value)}
            fullWidth
            required
            sx={oInputAuth}
          />
        </MDBox>

        {loiForm && (
          <MDBox mt={2}>
            <MDAlert color="error">{loiForm}</MDAlert>
          </MDBox>
        )}

        <MDButton
          type="submit"
          variant="gradient"
          color="info"
          fullWidth
          disabled={dangGui}
          sx={{ mt: 4, ...sxNutAuth }}
        >
          {dangGui ? "Đang lưu…" : "Đặt lại mật khẩu"}
        </MDButton>
      </MDBox>
    </KhungTrang>
  );
}

export default DatLaiMatKhau;
